import { Injectable } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { ServerConnectionService } from '../server-connection.service';
import { CoreModule } from '../../core.module';

@Injectable({
	providedIn: CoreModule,
})
export class SettingsService {
	private cartPurchaseEnabled;

	private _settingsStore = <ReplaySubject<Object>> new ReplaySubject(1);

	private dataStore = {
		store: [],
	};

	// Cache for all config settings
	private configCache: { [key: string]: string } = null;

	private configLoadPromise: Promise<void> = null;

	constructor(
		private scs: ServerConnectionService,
	) {
	}

	get settingsStore$() {
		return this._settingsStore.asObservable();
	}

	/**
	 * Fetches all config settings from the server in a single request
	 * and caches them for subsequent use
	 */
	public fetchConfig(): Promise<void> {
		// If already loaded, return immediately
		if (this.configCache !== null) {
			return Promise.resolve();
		}

		// If currently loading, return the existing promise
		if (this.configLoadPromise !== null) {
			return this.configLoadPromise;
		}

		// Start loading
		this.configLoadPromise = new Promise((resolve, reject) => {
			this.scs.http$('GET', '/api/settings/config', null, null).subscribe(
				(result: { success: boolean; config: { [key: string]: string } }) => {
					if (result.success && result.config) {
						this.configCache = result.config;
						resolve();
					} else {
						reject(new Error('Failed to fetch config'));
					}
				},
				(error) => {
					reject(error);
				},
			);
		});

		return this.configLoadPromise;
	}

	public async fetchCartPurchaseSetting(): Promise<boolean> {
		const value = await this.getConfigValue('cart_purchase');
		return this.parseSettingAsBoolean(value);
	}

	public async promoWizardSetting(): Promise<boolean> {
		const value = await this.getConfigValue('display_promo_wizard');
		return this.parseSettingAsBoolean(value);
	}

	public async resourcesSetting(): Promise<boolean> {
		const value = await this.getConfigValue('display_resources');
		return this.parseSettingAsBoolean(value);
	}

	public async getLoginSettings(): Promise<void> {
		const value = await this.getConfigValue('show_login_ab');
		this.dataStore.store.push({
			name: 'show_login_ab',
			value: this.parseSettingAsBoolean(value),
		});
		this._settingsStore.next(this.dataStore.store);
	}

	public async getExperimentSettings(): Promise<string | null> {
		return this.getConfigValue('abtesting');
	}

	public async getPaypalButton(): Promise<string | null> {
		return this.getConfigValue('paypal_button');
	}

	public async purchaseFlowStep2V2(): Promise<boolean> {
		const value = await this.getConfigValue('purchase_flow_step2_v2');
		return this.parseSettingAsBoolean(value);
	}

	public async priceForGoldPlatinumStep2Setting(): Promise<boolean> {
		const value = await this.getConfigValue('display_gold_platinum_price_column');
		return this.parseSettingAsBoolean(value);
	}

	public async priceForFreeGuestStep2Setting(): Promise<boolean> {
		const value = await this.getConfigValue('display_free_guest_price_column');
		return this.parseSettingAsBoolean(value);
	}

	public async getMembershipUpsellSetting(): Promise<boolean> {
		const value = await this.getConfigValue('display_membership_upsell');
		return this.parseSettingAsBoolean(value);
	}

	public async getCheckoutSignupLoginSetting(): Promise<boolean> {
		const value = await this.getConfigValue('display_checkout_signup_login');
		return this.parseSettingAsBoolean(value);
	}

	public async getAuthorProfileCTASetting(): Promise<boolean> {
		const value = await this.getConfigValue('display_author_profile_cta');
		return this.parseSettingAsBoolean(value);
	}

	public async getNewFeatureValidationSetting(): Promise<boolean> {
		const value = await this.getConfigValue('enable_new_feature_validation');
		return this.parseSettingAsBoolean(value);
	}

	public async eventRegistrationSetting(): Promise<boolean> {
		const value = await this.getConfigValue('display_event_registration');
		return this.parseSettingAsBoolean(value);
	}

	public async getShowLinkOptionsSetting(): Promise<boolean> {
		const value = await this.getConfigValue('show_link_options');
		return this.parseSettingAsBoolean(value);
	}

	public async getRecurringPromotionsSetting(): Promise<string> {
		const value = await this.getConfigValue('recurring_features');
		return value || 'none';
	}

	public async displayPromoStacksModalForFeatures(): Promise<boolean> {
		const value = await this.getConfigValue('display_promo_stacks_modal_for_features');
		return this.parseSettingAsBoolean(value);
	}

	public async getMemberDiscountSettings(type: 'gold' | 'platinum'): Promise<string | null> {
		const target = type === 'platinum' ? 'platinum_member_discount' : 'gold_member_discount';
		return this.getConfigValue(target);
	}

	public async stripeCheckoutEnabled(): Promise<boolean> {
		const value = await this.getConfigValue('stripe_checkout_enabled');
		return this.parseSettingAsBoolean(value);
	}

	public async displayImportAuthorBooksSetting(): Promise<boolean> {
		const value = await this.getConfigValue('display_import_author_books');
		return this.parseSettingAsBoolean(value);
	}

	/**
	 * Gets a setting value from the cache, loading config if needed
	 */
	private async getConfigValue(settingName: string): Promise<string | null> {
		await this.fetchConfig();
		return this.configCache[settingName] || null;
	}

	/**
	 * Converts a setting value to boolean
	 */
	private parseSettingAsBoolean(value: string | null): boolean {
		if (!value) {
			return false;
		}
		if (typeof value === 'string') {
			return value.trim().toLowerCase() === 'true';
		}
		return Boolean(value);
	}
}
